import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import PublicPageLayout from '../components/PublicPageLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ChevronRight, Send, Mail, MapPin, Phone, Clock, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const contactInfo = [
  { icon: Mail, label: 'Email', value: 'support@expensiq.com.au', color: 'text-emerald-500', bg: 'bg-emerald-50' },
  { icon: MapPin, label: 'Location', value: 'Sydney, NSW, Australia', color: 'text-blue-500', bg: 'bg-blue-50' },
  { icon: Phone, label: 'Phone', value: '+61 2 XXXX XXXX', color: 'text-purple-500', bg: 'bg-purple-50' },
  { icon: Clock, label: 'Support Hours', value: 'Mon–Fri, 9am–5pm AEST', color: 'text-amber-500', bg: 'bg-amber-50' },
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/api/contact`, form);
      setSent(true);
      toast.success('Message sent successfully!');
    } catch {
      toast.error('Failed to send message. Please try again.');
    }
    setSending(false);
  };

  return (
    <PublicPageLayout>
      <div data-testid="contact-page">
        {/* Hero */}
        <section className="bg-gradient-to-b from-slate-50 to-white py-16">
          <div className="max-w-4xl mx-auto px-6">
            <div className="flex items-center gap-2 text-sm text-slate-400 mb-8">
              <Link to="/" className="hover:text-slate-600 transition-colors">Home</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-slate-600 font-medium">Contact Us</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Get in Touch</h1>
            <p className="text-base text-slate-500 max-w-xl">
              Have a question, feedback, or need support? We'd love to hear from you. Our team is here to help.
            </p>
          </div>
        </section>

        <section className="py-16">
          <div className="max-w-4xl mx-auto px-6">
            <div className="grid md:grid-cols-5 gap-10">
              {/* Contact Info */}
              <div className="md:col-span-2 space-y-4">
                <h2 className="text-lg font-bold text-slate-900 mb-5">Contact Information</h2>
                {contactInfo.map(c => (
                  <div key={c.label} className="flex items-start gap-3">
                    <div className={`${c.bg} p-2.5 rounded-xl flex-shrink-0`}>
                      <c.icon className={`w-5 h-5 ${c.color}`} />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 uppercase tracking-wider font-medium">{c.label}</p>
                      <p className="text-sm text-slate-700 font-medium mt-0.5">{c.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Form */}
              <div className="md:col-span-3">
                {sent ? (
                  <div className="text-center py-16 bg-emerald-50 rounded-2xl border border-emerald-200" data-testid="contact-success">
                    <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-slate-900 mb-2">Message Sent!</h3>
                    <p className="text-sm text-slate-500 mb-6">Thank you for reaching out. We'll get back to you within 24 hours.</p>
                    <button onClick={() => { setSent(false); setForm({ name: '', email: '', subject: '', message: '' }); }} className="text-sm text-emerald-600 hover:text-emerald-700 font-medium">
                      Send another message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-slate-700 mb-1.5 block">Name *</label>
                        <Input
                          value={form.name}
                          onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                          placeholder="Your name"
                          className="bg-white border-slate-200"
                          data-testid="contact-name-input"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700 mb-1.5 block">Email *</label>
                        <Input
                          type="email"
                          value={form.email}
                          onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                          placeholder="you@example.com"
                          className="bg-white border-slate-200"
                          data-testid="contact-email-input"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1.5 block">Subject</label>
                      <Input
                        value={form.subject}
                        onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                        placeholder="How can we help?"
                        className="bg-white border-slate-200"
                        data-testid="contact-subject-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1.5 block">Message *</label>
                      <textarea
                        value={form.message}
                        onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                        placeholder="Tell us more..."
                        rows={5}
                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 resize-none"
                        data-testid="contact-message-input"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={sending}
                      className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-3 rounded-xl font-semibold"
                      data-testid="contact-submit-button"
                    >
                      {sending ? 'Sending...' : <><Send className="w-4 h-4 mr-2" /> Send Message</>}
                    </Button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
    </PublicPageLayout>
  );
}
